import InView from 'inview';
import { CountUp } from 'countup.js/dist/countUp.umd';

/**
 * Parse percentage value from stat string
 * Handles formats like "99%", ">99%", "100%", "89%"
 * @param {string} value - The stat value string
 * @returns {number} - The percentage as a decimal (0-1)
 */
function parsePercentage(value) {
	if (!value) {
		return 0;
	}

	// Remove any non-numeric characters except decimal point and > symbol
	// Handle ">99%" by extracting just the number
	const cleaned = value.replace(/[^0-9.]/g, '');
	const percentage = parseFloat(cleaned);

	// If we have a valid number, convert to decimal (99 -> 0.99)
	if (!Number.isNaN(percentage)) {
		return percentage / 100;
	}

	return 0;
}

/**
 * Calculate stroke-dasharray for pie chart
 * @param {number} percentage - Percentage as decimal (0-1)
 * @param {number} circumference - Total circumference of the circle
 * @returns {string} - stroke-dasharray value (e.g., "451.44 456")
 */
function calculateStrokeDasharray(percentage, circumference) {
	const dashLength = circumference * percentage;
	return `${dashLength} ${circumference}`;
}

export class Stat {
	isAnimated = false;

	pieCircle = null;

	numberElement = null;

	countUp = null;

	circumference = 456;

	constructor(el) {
		if (el) {
			this.el = el;
			this.statValue = this.el.dataset.statValue || '';
			this.pieCircle = this.el.querySelector('.pro-stat-pie');
			this.numberElement = this.el.querySelector('.js-counter[data-number]');

			if (this.pieCircle) {
				// Get circumference from data attribute or use default
				const circumferenceAttr = this.pieCircle.dataset.circumference;
				if (circumferenceAttr) {
					this.circumference = parseFloat(circumferenceAttr);
				}

				// Initialize pie chart animation
				this.initPieChart();
			}

			if (this.numberElement) {
				// Initialize count-up animation
				this.initCountUp();
			}

			// Set up InView observer
			this.inView = InView(this.el, this.handleInView);
		}
	}

	initCountUp() {
		if (!this.numberElement) return;

		const numberValue = this.numberElement.dataset.number;
		if (!numberValue) {
			return;
		}

		// Determine decimal places
		let decimalPlaces = 0;
		const decimalIndex = numberValue.indexOf('.');
		if (decimalIndex > 0) {
			decimalPlaces = numberValue.length - decimalIndex - 1;
		}

		// Check if value has a prefix like ">" and store it
		const originalValue = this.statValue;
		const hasPrefix = originalValue.includes('>') || originalValue.includes('<');
		let prefix = '';
		if (hasPrefix) {
			if (originalValue.includes('>')) {
				prefix = '>';
			} else if (originalValue.includes('<')) {
				prefix = '<';
			}
		}

		// Store prefix and suffix for display
		this.numberPrefix = prefix;
		this.numberSuffix = originalValue.includes('%') ? '%' : '';

		const numericValue = parseFloat(numberValue);
		if (Number.isNaN(numericValue)) {
			return;
		}

		// Store formatting info for use in update function
		this.countUpDecimalPlaces = decimalPlaces;
		this.countUpPrefix = prefix;
		this.countUpSuffix = this.numberSuffix;

		// Set up MutationObserver to preserve prefix/suffix when CountUp updates the element
		const self = this;
		this.formatNumber = (textContent) => {
			// Extract just the number from the text
			const numberMatch = textContent.match(/[\d.]+/);
			if (numberMatch) {
				const numValue = parseFloat(numberMatch[0]);
				let formattedValue = numValue.toFixed(decimalPlaces);
				if (decimalPlaces === 0) {
					formattedValue = Math.round(numValue).toString();
				}
				return `${prefix}${formattedValue}${this.numberSuffix}`;
			}
			return textContent;
		};

		// Create MutationObserver to watch for text changes (backup in case updateFn is bypassed)
		// This will be disconnected during animation to prevent jitter
		let isUpdating = false;
		this.mutationObserver = new MutationObserver((mutations) => {
			if (isUpdating || this.isAnimating) return; // Prevent infinite loop and don't interfere during animation

			mutations.forEach((mutation) => {
				if (mutation.type === 'childList' || mutation.type === 'characterData') {
					const currentText = this.numberElement.textContent;
					// Only reformat if it doesn't already have our prefix/suffix
					if (!currentText.startsWith(prefix) || !currentText.endsWith(this.numberSuffix)) {
						isUpdating = true;
						const formatted = this.formatNumber(currentText);
						if (formatted !== currentText) {
							this.numberElement.textContent = formatted;
						}
						// Reset flag after a brief delay
						setTimeout(() => {
							isUpdating = false;
						}, 10);
					}
				}
			});
		});

		// Start observing (will be disconnected during animation)
		this.mutationObserver.observe(this.numberElement, {
			childList: true,
			characterData: true,
			subtree: true,
		});

		// Set initial value with prefix and suffix
		this.numberElement.textContent = `${prefix}0${this.numberSuffix}`;

		// Initialize CountUp with prefix and suffix if supported, otherwise we'll use updateFn
		// Note: self is already declared above for formatNumber
		const countUpOptions = {
			decimalPlaces,
			duration: 2, // 2 seconds animation
		};

		// Try using CountUp's built-in prefix/suffix if available
		if (prefix) {
			countUpOptions.prefix = prefix;
		}
		if (this.numberSuffix) {
			countUpOptions.suffix = this.numberSuffix;
		}

		this.countUp = new CountUp(this.numberElement, numericValue, countUpOptions);

		// Override the updateFn to ensure prefix and suffix are always applied
		// This is critical because CountUp might bypass prefix/suffix in some cases
		this.countUp.updateFn = function (value) {
			// Format the value with proper decimal places
			let formattedValue = value.toFixed(self.countUpDecimalPlaces);
			// Remove trailing zeros if no decimals needed
			if (self.countUpDecimalPlaces === 0) {
				formattedValue = Math.round(value).toString();
			}
			// Always apply prefix and suffix - this ensures they're never lost
			const formatted = `${self.countUpPrefix}${formattedValue}${self.countUpSuffix}`;
			self.numberElement.textContent = formatted;
		};
	}

	initPieChart() {
		if (!this.pieCircle || !this.statValue) {
			return;
		}

		// Parse the percentage value
		const percentage = parsePercentage(this.statValue);

		// Calculate and set the stroke-dasharray
		const strokeDasharray = calculateStrokeDasharray(percentage, this.circumference);

		// Set initial state (0) - will be animated when in view
		const initialDasharray = `0 ${this.circumference}`;
		this.pieCircle.style.strokeDasharray = initialDasharray;
		this.targetDasharray = strokeDasharray;
	}

	handleInView = (isInView) => {
		if (isInView && !this.isAnimated) {
			this.isAnimated = true;
			this.isAnimating = true;

			// Disconnect MutationObserver during animation to prevent jitter
			if (this.mutationObserver) {
				this.mutationObserver.disconnect();
			}

			// Use requestAnimationFrame to ensure the initial state is rendered first
			requestAnimationFrame(() => {
				// Animate pie chart
				if (this.pieCircle && this.targetDasharray) {
					this.pieCircle.style.strokeDasharray = this.targetDasharray;
				}

				// Start count-up animation
				if (this.countUp) {
					// Ensure updateFn is set before starting (critical for prefix/suffix)
					const self = this;
					this.countUp.updateFn = function (value) {
						// Format the value with proper decimal places
						let formattedValue = value.toFixed(self.countUpDecimalPlaces);
						// Remove trailing zeros if no decimals needed
						if (self.countUpDecimalPlaces === 0) {
							formattedValue = Math.round(value).toString();
						}
						// Always apply prefix and suffix - this is critical
						const formatted = `${self.countUpPrefix}${formattedValue}${self.countUpSuffix}`;
						// Set directly - CountUp handles timing, don't add requestAnimationFrame here
						self.numberElement.textContent = formatted;
					};

					// Ensure initial value has prefix/suffix before starting
					// This is important because CountUp might reset the element
					this.numberElement.textContent = `${this.countUpPrefix}0${this.countUpSuffix}`;

					// Start animation immediately - updateFn will handle prefix/suffix
					const startPromise = this.countUp.start();
					if (startPromise && typeof startPromise.then === 'function') {
						// CountUp returns a Promise
						startPromise.then(() => {
							this.isAnimating = false;
							// Reconnect observer after a brief delay to catch any final updates
							setTimeout(() => {
								if (this.mutationObserver && this.numberElement) {
									this.mutationObserver.observe(this.numberElement, {
										childList: true,
										characterData: true,
										subtree: true,
									});
								}
							}, 100);
						}).catch(() => {
							this.isAnimating = false;
						});
					} else {
						// Fallback: use duration to estimate completion time
						setTimeout(() => {
							this.isAnimating = false;
							if (this.mutationObserver && this.numberElement) {
								this.mutationObserver.observe(this.numberElement, {
									childList: true,
									characterData: true,
									subtree: true,
								});
							}
						}, 2100); // Duration (2000ms) + small buffer
					}
				} else {
					// If no countUp, just reset the flag
					this.isAnimating = false;
				}
			});
		}
	};
}

export function initStats() {
	const statEls = [...document.querySelectorAll('.pro-stat-stat[data-stat-value]')];

	if (statEls.length) {
		// Initialize embark.components.stats if it doesn't exist
		if (!embark.components) {
			embark.components = {};
		}

		embark.components.stats = statEls.map((el) => {
			// Skip if inside a tabpanel (unless in admin)
			if (el.closest('.wp-block-embark-tabpanel') && !document.body.classList.contains('wp-admin')) {
				return null;
			}

			return new Stat(el);
		}).filter(Boolean);
	}
}

document.addEventListener('DOMContentLoaded', initStats);
